import { useContext, useEffect, useMemo, useState } from "react";
import Loading from "common/Loading";
import Legend from "common/graphs/Legend";
import { ChartDownsampled } from "./Chart";
import { parseStatsCpuData } from "./api";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import { PERIOD_TO_LINES } from "common/constants";
import { atBqnTarget } from "common/api";

const Empty = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const ChartContainer = styled.div`
  flex: 100% 1 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  // Chart vertical margins (cpuChartDefaultMargin top 20 + bottom 30)
  // are drawn inside the container, so add them to the target plot height.
  min-height: calc(${props => props.big ? 435 : 233}px + 50px);

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const LegendContainer = styled.div`
  width: 100%;
  padding: 0px 55px 0px 55px;
`;

const ChartAndLegendContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  white-space: nowrap;
`;

export const CpuChart = ({
  bqnId,
  period,
  setCpuChoices,
  range,
  initialRange,
  chart,
  cpu,
  mode,
  scale,
  big,
}) => {
  const [data, setData] = useState({ fields: [], items: [] });
  const [legendFieldsSet, setLegendFieldsSet] = useState(new Set());
  const colorsSet = login.isTheme("light") ? colors : colorsDark;
  const [loading, setLoading] = useState(false);
  const actions = useContext(ActionsContext);

  const doLoad = () => {
    setLoading(true);
    setData({ fields: [], items: [] });
    const { from, to } = initialRange;
    const chartN = chart === "busy" ? "idle" : chart;
    ifCl
      .run(
        atBqnTarget(
          `show statistics cpu ${chartN} lines ${PERIOD_TO_LINES[period]}`,
          mode,
          bqnId,
        ),
      )
      .then((response) => {
        const { items, fields } = parseStatsCpuData(response, from, to);
        const [, ...cpuNames] = fields;
        if (cpuNames?.length > 0) {
          /** Nodes can have different CPU counts, so add its own CPU options */
          setCpuChoices((current) => {
            const currentCpus = current.map(({ value }) => value);
            const cpuChoicesUpdate = Array.from(
              new Set([...currentCpus, ...cpuNames]),
            );

            if (currentCpus.join() === cpuChoicesUpdate.join()) {
              return current;
            } else {
              return cpuChoicesUpdate.map((name) => ({
                label: name,
                value: name,
              }));
            }
          });
        }
        setData({ items, fields });
        setLoading(false);
      })
      .catch((error) => {
        console.warn(
          `Cpu statistics lines retrieval failed: ${
            !error.message ? "unknown reason" : error.message
          }`,
        );
        setLoading(false);
      });
  };

  const { fields, items } = data;

  const newFieldsValues = fields.map((field, index) => ({
    name: field,
    label: field,
    color: colorsSet[index % colorsSet.length],
  }));

  const selectedFields = cpu === "All" ? fields : fields.filter((item) => item === cpu);
  const newFields = newFieldsValues.filter(({ name }) => selectedFields.includes(name));

  useEffect(() => {
    if (fields.length > 0) {
      setLegendFieldsSet(new Set(selectedFields));
    }
  }, [cpu, fields.join(",")]);

  const transformedItems = useMemo(() => {
    if (chart !== "busy" || items.length === 0) return items;
    return items.map((item) => ({
      time: item.time,
      ...Object.fromEntries(fields.map((f) => [f, Number.isFinite(item[f]) ? 100 - item[f] : null])),
    }));
  }, [items, fields, chart]);

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [bqnId, chart, initialRange]);

  const selectedRange = range || initialRange;
  const displayFields = newFields.filter(({ name }) => legendFieldsSet.has(name));

  return (
    <ChartAndLegendContainer>
      {!loading ? (
        <LegendContainer>
          <Legend
            fields={newFields}
            value={legendFieldsSet}
            onChange={(filterSet) => setLegendFieldsSet(filterSet)}
          />
        </LegendContainer>
      ) : null}
      <ChartContainer big={big}>
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>{"No Data Available"}</Empty>
        ) : (
          <ChartDownsampled
            fixedYRange={scale === "Full" ? [0, 100] : [0, null]}
            items={transformedItems}
            fields={displayFields}
            range={selectedRange}
          />
        )}
      </ChartContainer>
    </ChartAndLegendContainer>
  );
};
